import { Box, Chip } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { AGREEMENT_TYPE } from 'components/ClinicalServicesDropdown';
import { firestoreInstance } from 'config/ApiService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import MaterialTable from 'material-table';
import React from 'react';
import { USE_CASE_COLORS } from './PhioengagePatientList';
import { useNavigate } from 'react-router-dom';
import { logScreenView } from 'utils/gaLog';

const PhioengageDischargeConfigurationContainer = () => {
    const [customerList, setCustomerList] = React.useState<any[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);

    const navigate = useNavigate();

    React.useEffect(() => {
        (async () => {
            const ref = collection(firestoreInstance(), 'clients');
            const q = query(
                ref,
                where('agreement_type', 'in', [
                    AGREEMENT_TYPE.ENGAGE,
                    AGREEMENT_TYPE.ACCESS_AND_ENGAGE,
                    AGREEMENT_TYPE.ACCESS_ENGAGE_AND_COLLECT
                ])
            );
            const snapshots = await getDocs(q);
            const clients: any[] = [];
            snapshots.forEach((doc) => {
                const data = doc.data();
                clients.push({
                    ...data,
                    id: doc.id,
                    name: data.name,
                    agreement_type: data.agreement_type
                });
            });
            const withPresets: any[] = [];
            for (const client of clients) {
                const uiConfig = await getDocs(
                    query(
                        collection(firestoreInstance(), 'web_ui_configs'),
                        where('client_id', '==', client.id)
                    )
                );
                if (!uiConfig.empty) {
                    const configData = uiConfig.docs[0].data();
                    if (configData.presets) {
                        const presets = configData.presets;
                        // Here you can process the presets as needed
                        withPresets.push({
                            ...client,
                            preset: presets
                        });
                    } else {
                        withPresets.push({
                            ...client,
                            preset: undefined
                        });
                    }
                }
            }
            setCustomerList(withPresets);
            setIsLoading(false);
        })();
    }, []);

    // Page view logging
    React.useEffect(() => {
        // Log screen view event
        logScreenView('discharge_config_page_view');
    }, []);

    return (
        <Box minWidth="650px">
            <MaterialTable
                data={customerList}
                columns={[
                    { field: 'name', title: 'Name' },
                    {
                        field: 'preset',
                        title: 'Use Case',
                        render: (rowData: any) => {
                            if (!rowData.preset) {
                                return '';
                            }
                            return (
                                <Chip
                                    label={rowData.preset.split('_').join(' ')}
                                    size="small"
                                    sx={{
                                        textTransform: 'capitalize',
                                        border: `2px solid ${USE_CASE_COLORS[`${rowData.preset}`]}`
                                    }}
                                    variant="outlined"
                                />
                            );
                        }
                    },
                    {
                        field: 'clinical_services_enabled',
                        title: 'EQL Clinical Services',
                        render: (rowData: any) => {
                            if (!rowData.clinical_services_enabled) {
                                return 'Disabled';
                            }
                            return 'Enabled';
                        }
                    }
                ]}
                title="Discharge Behaviour and Templates"
                options={{
                    pageSize: 20,
                    pageSizeOptions: [20, 40, 60],
                    searchFieldVariant: 'outlined',
                    filtering: false,
                    showFirstLastPageButtons: false
                }}
                actions={[
                    {
                        icon: 'edit',
                        tooltip: 'Edit',
                        onClick: (event, rowData) => {
                            navigate(`/dashboard/phio-engage-discharge-settings/${rowData.id}`, {
                                state: {
                                    clientId: rowData.id,
                                    clientName: rowData.name,
                                    preset: rowData.preset
                                }
                            });
                        }
                    }
                ]}
            />
            <Backdrop
                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                open={isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default PhioengageDischargeConfigurationContainer;
